import React, { useCallback, useMemo, useEffect, useState } from 'react';
import * as yup from 'yup';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { DatePicker } from 'mui-rff';
import DateFnsUtils from '@date-io/date-fns';
import enLocale from 'date-fns/locale/en-GB';
import cc from 'classnames';
import { ThemeProvider, useTheme } from '@material-ui/core/styles';
import { createTheme, useMediaQuery, CircularProgress } from '@material-ui/core';

import { AxiosStatus } from 'api/axiosConfig';
import Form from 'ui/Form/Form';
import { requiredOnly, validationCallback } from 'utils/validation';
import Button, { Themes } from 'ui/Button/Button';
import { changeFieldValue } from 'utils/formMutators';
import Input, { Select, SelectItem } from 'ui/Input';
import {
  ICreateInvoiceResponse,
  IGhanaPaymentsState,
  IServiceName,
  ITaxTypes,
  ITaxTypesResponse,
} from 'components/modules/BillsPayment/tabs/GhanaGov/GhanaGov.model';
import api from 'api/api';
import { ModalWindow, StatusModal } from 'components/modals';
import { IModalProps } from '../IModalProps';
import { IFormValues, IInvoiceRequestValues } from './GenerateInvoiceModal.model';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './GenerateInvoiceModal.module.css';

import calendar from 'assets/img/calendar.png';

interface PropsType {
  serviceName: string;
  state: IGhanaPaymentsState;
  onCloseAfterGettingInvoice: (invoiceNumber: string) => void;
}

type IStatusModalProps = {
  open: boolean;
  status: 'success' | 'failure';
  content: React.ReactChild;
  onRetry: () => void;
};

const initStatusModal: IStatusModalProps = {
  open: false,
  status: 'failure',
  content: '',
  onRetry: () => null,
};

const GenerateInvoiceModal = ({
  open,
  onClose,
  onCloseAfterGettingInvoice,
  serviceName,
  state,
}: IModalProps & PropsType) => {
  const [currentService, setCurrentService] = useState<IServiceName | null>(null);
  const [taxTypes, setTaxTypes] = useState<ITaxTypes[]>([]);
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [statusModalState, setStatusModalState] = useState<IStatusModalProps>(initStatusModal);

  const closeStatusModal = useCallback(() => setStatusModalState(initStatusModal), []);

  useEffect(() => {
    const filteredServices = state.serviceName.filter((item) => item.code === serviceName);
    setCurrentService(filteredServices[0]);
  }, [state, serviceName, currentService]);

  useEffect(() => {
    if (currentService?.id) {
      api
        .get(`billspayment/taxTypes?serviceId=${currentService.id}`, {}, true, false)
        .then((response: ITaxTypesResponse) => {
          setTaxTypes(response.taxTypes);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [currentService]);

  const dynamicSchema = useCallback(() => {
    switch (serviceName) {
      case 'TOURISM_LEVY':
        return yup.object().shape({
          accountReference: requiredOnly('Levy Number'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      case 'CUSTOMS_DECLARATION_UNIPASS':
        return yup.object().shape({
          billNumber: requiredOnly('Bill Number'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });

      case 'Direct Taxes':
        return yup.object().shape({
          taxType: requiredOnly('Tax Type'),
          tin: requiredOnly('TIN'),
          period: requiredOnly('Period'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });

      case 'Indirect Taxes':
        return yup.object().shape({
          taxType: requiredOnly('Tax type'),
          tin: requiredOnly('TIN / Ghana Card Number'),
          period: requiredOnly('Period'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      case 'VEHICLE_INCOME_TAX':
        return yup.object().shape({
          vehicleRegistrationNumber: requiredOnly('Vehicle Number'),
          paymentPeriod: requiredOnly('Payment period'),
          period: requiredOnly('Period'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      case 'Domestic Taxes':
        return yup.object().shape({
          taxType: requiredOnly('Tax Type'),
          tin: requiredOnly('TIN / Ghana Card Number'),
          period: requiredOnly('Period'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      case 'Pay for an Existing RGD Application':
        return yup.object().shape({
          transactionNumber: requiredOnly('Transaction Number'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      case 'LANDS COMMISSION':
        return yup.object().shape({
          billNumber: requiredOnly('Bill Number'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      case 'Stamp Duty':
        return yup.object().shape({
          billNumber: requiredOnly('Bill Number'),
          tin: requiredOnly('TIN / Ghana Card Number'),
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
      default:
        return yup.object().shape({
          firstName: requiredOnly('First Name'),
          lastName: requiredOnly('Last Name'),
          phoneNumber: requiredOnly('Phone Number'),
        });
    }
  }, [serviceName]);

  const validate = useCallback(
    (values: IFormValues) => validationCallback<IFormValues>(dynamicSchema())(values),
    [dynamicSchema],
  );

  const intialValues: IFormValues = useMemo(() => {
    const startValues = {
      firstName: String(sessionStorage.firstName),
      lastName: String(sessionStorage.lastName),
      phoneNumber: String(sessionStorage.phoneNumber),
    };

    return startValues;
  }, []);

  const onSubmit = (values: IInvoiceRequestValues) => {
    const requestData: IInvoiceRequestValues = {
      serviceCode: serviceName,
      ...values,
    };

    setSubmitStatus(AxiosStatus.PENDING);

    api
      .post('billspayment/createInvoice', requestData)
      .then((data: ICreateInvoiceResponse) => {
        setSubmitStatus(AxiosStatus.SUCCESS);
        onCloseAfterGettingInvoice(data.invoiceNumber);
      })
      .catch((err: ICreateInvoiceResponse) => {
        setStatusModalState({
          status: 'failure',
          open: true,
          content: err.description,
          onRetry: () => {
            closeStatusModal();
          },
        });
        setSubmitStatus(AxiosStatus.FAILURE);
      });
  };

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const materialTheme = createTheme({
    overrides: {
      MuiFilledInput: {
        root: {
          backgroundColor: '#F6F7F9',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#F6F7F9',
          },
          '&.Mui-focused': {
            backgroundColor: '#F6F7F9',
          },
          '&.Mui-error': {
            border: '1px solid #f44336',
          },
        },
        input: {
          height: '23px',
          fontSize: '14px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#222736',
          cursor: 'pointer',
        },
      },
      MuiInputLabel: {
        root: {
          height: '16px',
          fontSize: '12px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#7D8598',
          '&.Mui-focused': {
            color: '#7D8598',
          },
          '&.Mui-error.Mui-focused': {
            color: '#f44336',
          },
        },
        formControl: {
          top: '5px',
        },
      },
      MuiPaper: {
        root: {
          width: '368px',
          height: '405px',
          marginTop: smallScreen ? 0 : '8px',
          marginLeft: smallScreen ? 0 : '15px',
          border: '1px solid #E5E5E5',
          boxSizing: 'border-box',
          borderRadius: '4px',
        },
      },
      MuiPickersBasePicker: {
        pickerView: {
          maxWidth: '100%',
          maxHeight: '100%',
          overflowX: 'unset',
        },
      },
      MuiPickersCalendarHeader: {
        switchHeader: {
          height: '24px',
          width: '336px',
          margin: smallScreen ? '0 3px' : '0 16px',
          marginTop: '24px',
          marginBottom: '24px',
        },
        daysHeader: {
          maxHeight: '24px',
          margin: smallScreen ? '0 3px' : '0 16px',
        },
        dayLabel: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '24px',
          margin: 0,
          fontSize: '12px',
          fontFamily: 'var(--ff-etelka-text)',
        },
        iconButton: {
          padding: 0,
        },
      },
      MuiTypography: {
        body1: {
          fontSize: '14px',
          lineHeight: '24px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#222736',
        },
        body2: {
          fontSize: '14px',
          fontFamily: 'var(--ff-etelka-text)',
          color: '#222736',
        },
      },
      MuiPickersDay: {
        daySelected: {
          backgroundColor: '#4F2651',
          '&:hover': {
            backgroundColor: '#4F2651',
          },
        },
        day: {
          width: '48px',
          height: '48px',
          margin: 0,
        },
        current: {
          color: '#4F2651',
        },
      },
      MuiPickersCalendar: {
        week: {
          height: '48px',
          margin: '0 16px',
        },
      },
    },
  });

  const renderOptionalFields = (service: IServiceName | null) => {
    if (service) {
      return service.serviceFields.map(({ displayName, fieldName, id }) => {
        const correctFieldName = fieldName[0].toLocaleLowerCase() + fieldName.slice(1);

        if (fieldName === 'TaxType') {
          return (
            <Select name={correctFieldName} label={displayName} className={styles.field}>
              {taxTypes.map(({ name, id: itemId }) => (
                <SelectItem value={name} key={itemId}>
                  {name}
                </SelectItem>
              ))}
            </Select>
          );
        }
        if (fieldName === 'PaymentPeriod') {
          return (
            <Select name={correctFieldName} label={displayName} className={styles.field} disabled={submitting}>
              <SelectItem value="Anually" key="Anually">
                Anually
              </SelectItem>
              <SelectItem value="Quarterly" key="Quarterly">
                Quarterly
              </SelectItem>
            </Select>
          );
        }
        if (fieldName === 'Period') {
          return (
            <>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enLocale}>
                <ThemeProvider theme={materialTheme}>
                  <DatePicker
                    name="period"
                    label="Period"
                    disabled={submitting}
                    format="yyyy-MM"
                    size="small"
                    inputVariant="filled"
                    variant="inline"
                    views={['year', 'month']}
                    className={cc(styles.field)}
                    InputProps={{
                      endAdornment: <img src={calendar} alt="Calendar logo" />,
                      disableUnderline: true,
                      className: cc(styles.datePickerInput),
                      classes: { error: styles.errorDateField },
                    }}
                    InputLabelProps={{
                      className: cc(styles.datePicker),
                      classes: { error: styles.errorDateText },
                    }}
                    FormHelperTextProps={{
                      classes: { error: styles.errorDateHelperText },
                    }}
                    autoOk
                    disableToolbar
                    disableFuture
                    fullWidth
                  />
                </ThemeProvider>
              </MuiPickersUtilsProvider>
            </>
          );
        }
        return (
          <Input name={correctFieldName} label={displayName} className={styles.field} key={id} disabled={submitting} />
        );
      });
    }
    return null;
  };

  const renderFirstNameField = () => (
    <>
      <Input name="firstName" label="First Name" className={styles.field} disabled={submitting} />
    </>
  );

  const renderLastNameField = () => (
    <>
      <Input name="lastName" label="Last Name" className={styles.field} disabled={submitting} />
    </>
  );

  const renderPhoneNumberField = () => (
    <>
      <Input name="phoneNumber" label="Phone Number" className={styles.field} disabled={submitting} />
    </>
  );

  return (
    <ModalWindow title="Complete these fields to generate your Invoice Number" open={open} onClose={onClose}>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        mutators={{ changeFieldValue }}
        initialValues={intialValues}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {renderOptionalFields(currentService)}
            {renderFirstNameField()}
            {renderLastNameField()}
            {renderPhoneNumberField()}

            <div className={styles.btnWrapper}>
              <Button type="button" onClick={onClose} fullWidth>
                Cancel
              </Button>
              <Button
                type="submit"
                theme={Themes.secondary}
                className={styles.submitBtn}
                fullWidth
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
              </Button>
            </div>
          </form>
        )}
      />
      <StatusModal
        open={statusModalState.open}
        status={statusModalState.status}
        onRetry={statusModalState.onRetry}
        onClose={closeStatusModal}
        onRetryText="Close"
      >
        {statusModalState.content}
      </StatusModal>
    </ModalWindow>
  );
};

export default GenerateInvoiceModal;
